import { EditOutlined, UndoOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";

import { Button, Checkbox, Spin, message } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";

import CustomerConsentTermsModal from "@/components/CustomerConsentTermsModal";
import { DetailsCard } from "@/components/DetailsCard/DetailsCard";
import MykadModal from "@/components/MykadModal";
import PledgePdfPreview from "@/components/PledgePdfPreview";
import SignaturePad from "@/components/SignaturePad";
import { IMAGES, SUPPORTED_COUNTRIES } from "@/constant";
import styles from "@/pages/PledgeConsentApproval/PledgeConsentApproval.module.css";
import ConsentDetails from "@/pages/PledgeConsentApproval/components/ConsentDetails";
import CustomerDetails from "@/pages/PledgeConsentApproval/components/CustomerDetails";
import { RedeemPledgeDetailsForCustomerConsent } from "@/pages/PledgeConsentApproval/components/RedeemPledgeDetailsForCustomerConsent";
import { initiateSingpassSign } from "@/services/Auth/AuthApi";
import {
  getPledgeConsentDetails,
  getPledgeConsentStatus,
  updatePledgeConsent,
} from "@/services/Pledge/PledgeApis";
import { useTenantStore } from "@/stores/tenantStore";
import { PledgeBatch } from "@/types/pledgeService";

const PledgeConsentApproval: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [signaturePadModal, setSignaturePadModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pledgeList, setPledgeList] = useState<PledgeBatch[]>([]);
  const [myKadModal, setMykadModal] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pairId, setPairId] = useState("");
  const [openConsent, setOpenConsent] = useState(false);
  const [pendingAction, setPendingAction] = useState<null | "mykad" | "singpass" | "esign">(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // const [loadedCount, setLoadedCount] = useState(0);
  const [mykadEnabled, setMykadEnabled] = useState(false);
  const [actionType, setActionType] = useState<string>("");
  const lastScanError = useRef<string | null>(null);
  const activeScanRequest = useRef<string | null>(null);
  const COUNTRY = useTenantStore((state) => state.country);

  const handleConsentChange = (batch_id: number, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, batch_id] : prev.filter((x) => x !== batch_id)));
  };

  const onClickESignature = (): void => {
    const firstPledge: any = pledgeList?.[0]?.pledges?.[0];
    const targetCustomer: any = firstPledge?.redeemer || firstPledge?.customer;

    if (targetCustomer?.id_type?.toLowerCase() === "mykad") {
      message.error("Customer is onboarded through MyKad. Please sign using MyKad.");
      return;
    }
    setPendingAction("esign");
    setOpenConsent(true);
  };

  const selectedBatches = useMemo(
    () => pledgeList.filter((p) => selectedIds.includes(p.batch_id)),
    [pledgeList, selectedIds],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isSuccess = params.get("success") === "true" || params.get("status") === "success";

    if (isSuccess) {
      setSuccess(true);
      setLoading(false);
    }

    const xzParam = params.get("xz");
    if (xzParam) {
      try {
        const decodedValue = atob(xzParam);
        void submitPledgePairingId(decodedValue);
      } catch (err) {
        console.error("Invalid xz param");
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!pairId || pledgeList.length === 0 || success) {
      return;
    }

    const interval = window.setInterval(() => {
      void refreshStatus(pairId, pledgeList);
    }, 3000);

    return () => {
      window.clearInterval(interval);
    };
  }, [pairId, pledgeList, success]);

  const submitPledgePairingId = async (value: string): Promise<any> => {
    try {
      setLoading(true);
      const { pledge_details, ws_connection_code, is_mykad_enabled, action_type } =
        await getPledgeConsentDetails(value);
      if (pledge_details?.length) {
        setMykadEnabled(is_mykad_enabled);
        setPledgeList(pledge_details);
        setActionType(action_type);
        setSelectedIds(pledge_details.map((p: any) => p.batch_id));
        setPairId(ws_connection_code);
        void refreshStatus(ws_connection_code, pledge_details);
      } else {
        setPledgeList([]);
      }
    } catch (error) {
      console.error(error);
      setPledgeList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAmmendConsent = async (batchId: number): Promise<void> => {
    try {
      setLoading(true);
      await updatePledgeConsent(pairId, {
        consent_method: "ammend_pledge",
        batch_ids: [batchId],
        check_expiration: true,
      });
      if (pledgeList?.length === 1) {
        setPairId("");
        setPledgeList([]);
        setLoading(false);
      } else {
        await submitPledgePairingId(pairId);
      }
      message.success("Pledge has been marked for Amendment successfullly");
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleApproveConsent = async (
    url: string,
    consent_method = "e_signature",
  ): Promise<void> => {
    try {
      setLoading(true);
      await updatePledgeConsent(pairId, {
        batch_ids: selectedBatches.map((pledgeBatch) => pledgeBatch?.batch_id),
        customer_signature_media_url: url,
        consent_method,
        is_consent_provided: true,
        action_type: "create_pledge",
        check_expiration: true,
      });
      setSignaturePadModal(false);
      message.success("Pledge Consent has been approved successfullly");
      setPledgeList([]);
      setPairId("");
      const params = new URLSearchParams(location.search);
      const xzParam = params.get("xz");
      if (xzParam) {
        params.delete("xz");
        params.set("success", "true");
        navigate(
          {
            pathname: location.pathname,
            search: params.toString(),
          },
          { replace: true },
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onClickMyKad = (): void => {
    activeScanRequest.current = null;
    setMykadModal(true);
  };

  const refreshStatus = async (
    ws_connection_code = pairId,
    batchList = pledgeList,
    showLoading = false,
  ): Promise<void> => {
    try {
      if (showLoading) setLoading(true);
      const { responseData, is_expired, scan_in_progress, mykad_metadata } =
        await getPledgeConsentStatus({
          ws_connection_code,
          // batch_id: batchList.map((pledgeBatch) => pledgeBatch?.batch_id),
          batch_id: batchList?.[0]?.batch_id,
        });
      if (responseData?.status === "accepted" || responseData?.status === "rejected") {
        message.success("Your consent has been recorded successfully.");
        setMykadModal(false);
        setPledgeList([]);
        setPairId("");

        const params = new URLSearchParams(location.search);
        const xzParam = params.get("xz");
        if (xzParam) {
          params.delete("xz");
          params.set("success", "true");
          navigate(
            {
              pathname: location.pathname,
              search: params.toString(),
            },
            { replace: true },
          );
        }
      } else {
        const metadata = mykad_metadata ? JSON.parse(mykad_metadata) : null;
        if (metadata?.scan_error || metadata?.multi_failure) {
          // Saved errors can belong to a previous visit or an older request.
          if (!activeScanRequest.current || metadata.scan_request_id !== activeScanRequest.current)
            return;
          const error =
            metadata.scan_error ||
            "Fingerprint verification failed multiple times. Please ask the staff for assistance.";
          const key = JSON.stringify([ws_connection_code, metadata.scan_request_id, error]);
          if (lastScanError.current !== key) {
            lastScanError.current = key;
            message.error(error);
            activeScanRequest.current = null;
            setMykadModal(false);
          }
        } else if (is_expired && !scan_in_progress) {
          message.error("Customer consent has expired. Please request a new consent link.");
          setPairId("");
          setMykadModal(false);
        }
      }
    } catch (error) {
      console.error(error);
      const key = `${ws_connection_code}:status-check-error`;
      if (lastScanError.current !== key) {
        lastScanError.current = key;
        message.error("Unable to check fingerprint verification status. Please try again.");
        setMykadModal(false);
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleTriggerSingpass = async (): Promise<void> => {
    try {
      setLoading(true);
      const { signing_url } = await initiateSingpassSign({
        pledgeIds: selectedBatches
          .flatMap((batch) => batch.pledges)
          .map((pledge) => pledge.id)
          .join(","),
        connection_code: pairId,
        platform: "customer_web_app",
      });

      if (signing_url) {
        window.location.href = signing_url.toString();
      } else {
        console.error("Error initializing singpass customer creation");
      }
    } catch (error) {
      console.error("Error initializing singpass customer creation:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      {success ? (
        <div className={styles.invalidDivWrapper}>
          <div className={styles.invalidDivInnerWrapper}>
            <div className={styles.invalidDivContentWrapper}>
              <img src={IMAGES.SUCCESS_ICON} className={styles.linkLogo} />
              <p className={styles.invalidLinkHeader}>
                Your consent has been recorded successfully.
              </p>
              <p className={styles.invalidLinkContent}>
                Your consent has been recorded successfully. You can close this tab or the window.
              </p>
            </div>
          </div>
        </div>
      ) : pledgeList?.length > 0 ? (
        Boolean(pledgeList?.length) && (
          <div className={styles.pledgeListWrapper}>
            <div className={styles.pledgeListHeaderWrapper}>
              <div className="flex flex-col">
                <p className={styles.concentPageHeading}>Pledge Ticket Preview</p>
                <p
                  className={styles.noPledgesTitle}
                >{`Total Pledges for Consent - ${selectedIds?.length}`}</p>
              </div>
              {pledgeList.length && (
                <div className={styles.pledgeListButtonWrapper}>
                  {mykadEnabled && (
                    <Button
                      className={styles.mykadButton}
                      onClick={() => {
                        setPendingAction("mykad");
                        setOpenConsent(true);
                      }}
                    >
                      Sign with MyKad
                    </Button>
                  )}

                  {SUPPORTED_COUNTRIES.SG === COUNTRY && (
                    <Button
                      className={styles.signpassButton}
                      onClick={() => {
                        setPendingAction("singpass");
                        setOpenConsent(true);
                      }}
                    >
                      Sign with
                      <img src={IMAGES.SINGPASS_LOGO_WHITE} className={styles.signpassIcon} />
                    </Button>
                  )}

                  <Button className={styles.primaryBtn} onClick={onClickESignature}>
                    <EditOutlined /> Approve with e-signature
                  </Button>
                </div>
              )}
            </div>
            <div>
              {pledgeList.map((pledgeBatch) => {
                const customer = pledgeBatch?.pledges?.find((pledge) => pledge?.customer)?.customer;

                const owner = pledgeBatch?.pledges?.find((pledge) => pledge?.owner)?.owner;

                return (
                  <div key={pledgeBatch.batch_id}>
                    {/* Customer and Owner Details outside the batch card */}
                    {(customer || owner) && (
                      <div
                        className={`${styles.customerGrid} ${
                          customer && owner
                            ? styles.customerGridWithOwner
                            : styles.customerGridWithoutOwner
                        }`}
                      >
                        {customer && (
                          <CustomerDetails details={customer} variant="customerConsent" />
                        )}

                        {owner && (
                          <CustomerDetails
                            details={owner}
                            variant="customerConsent"
                            isWakil
                            customerName={customer?.name}
                          />
                        )}
                      </div>
                    )}

                    {/* Pledge Batch Card */}
                    <div className={styles.pledgeListInnerWrapper}>
                      <DetailsCard
                        inConsent
                        title={
                          <div className={styles.pledgeListInnerWrapperHeader}>
                            <span className={styles.pledgeListInnerTitle}>
                              {`Pledge Batch ID: ${pledgeBatch?.batch_id}`}{" "}
                              {`(${
                                actionType === "renew_pledge"
                                  ? "RENEWAL"
                                  : actionType === "redeem_pledge"
                                    ? "REDEMPTION"
                                    : actionType === "lost_ticket"
                                      ? "LOST TICKET"
                                      : "NEW PLEDGE"
                              })`}
                            </span>

                            <div>
                              <Button
                                className={styles.cancelBtn}
                                onClick={() => handleAmmendConsent(pledgeBatch?.batch_id)}
                              >
                                <UndoOutlined /> Amend
                              </Button>

                              <Checkbox
                                checked={selectedIds.includes(pledgeBatch.batch_id)}
                                onChange={(e) =>
                                  handleConsentChange(pledgeBatch.batch_id, e.target.checked)
                                }
                              >
                                Include for Consent
                              </Checkbox>
                            </div>
                          </div>
                        }
                        beforeColumns={
                          <div className={styles.pledgePDFPreview}>
                            {actionType === "redeem_pledge" ? (
                              <RedeemPledgeDetailsForCustomerConsent
                                details={pledgeBatch?.pledges}
                              />
                            ) : (
                              <ConsentDetails pledgeBatch={pledgeBatch} actionType={actionType} />
                            )}
                          </div>
                        }
                      />
                    </div>
                  </div>
                );
              })}
              {/* {pledgeList.map((pledgeBatch) => (
                <div className={styles.pledgeListInnerWrapper}>
                  <DetailsCard
                    title={
                      <div className={styles.pledgeListInnerWrapperHeader}>
                        <span
                          className={styles.pledgeListInnerTitle}
                        >{`Pledge Batch ID: ${pledgeBatch?.batch_id}`}</span>
                        <div>
                          {pledgeList.length && (
                            <Button
                              className={styles.cancelBtn}
                              onClick={() => handleAmmendConsent(pledgeBatch?.batch_id)}
                            >
                              <UndoOutlined /> Amend
                            </Button>
                          )}
                          <Checkbox
                            checked={selectedIds.includes(pledgeBatch.batch_id)}
                            onChange={(e) =>
                              handleConsentChange(pledgeBatch.batch_id, e.target.checked)
                            }
                          >
                            Include for Consent
                          </Checkbox>
                        </div>
                      </div>
                    }
                    beforeColumns={
                      <>
                        <div className={styles.pledgePDFPreview}>
                          {actionType === "redeem_pledge" ? (
                            <RedeemPledgeDetailsForCustomerConsent details={pledgeBatch?.pledges} />
                          ) : (
                            <ConsentDetails pledgeBatch={pledgeBatch} actionType={actionType} />
                          )}
                        </div>
                      </>
                    }
                  />
                </div>
              ))} */}
            </div>
          </div>
        )
      ) : loading ? (
        <div className="h-screen" />
      ) : (
        <div className={styles.invalidDivWrapper}>
          <div className={styles.invalidDivInnerWrapper}>
            <div className={styles.invalidDivContentWrapper}>
              <img src={IMAGES.BARCODE_IMAGE} className={styles.linkLogo} />
              <p className={styles.invalidLinkHeader}> Invalid or Expired URL</p>
              <p className={styles.invalidLinkContent}>
                The URL you are trying to access is either invalid or has expired. Please verify the
                URL and try again.
              </p>
            </div>
          </div>
        </div>
      )}

      {signaturePadModal ? (
        <SignaturePad
          open={signaturePadModal}
          onClose={() => setSignaturePadModal(false)}
          onSubmit={(url) => {
            void handleApproveConsent(url);
          }}
        />
      ) : null}

      {myKadModal && pairId && (
        <MykadModal
          pairId={pairId}
          open={myKadModal}
          mykadFinger={null}
          onClose={() => {
            activeScanRequest.current = null;
            setMykadModal(false);
          }}
          onScanRequest={(requestId) => {
            activeScanRequest.current = requestId;
          }}
          refreshStatus={refreshStatus}
          selectedBatches={selectedBatches}
        />
      )}
      {openConsent && (
        <CustomerConsentTermsModal
          open={openConsent}
          actionType={actionType}
          onClose={() => setOpenConsent(false)}
          onSubmit={() => {
            setOpenConsent(false);
            // Call the pending action after consent
            if (pendingAction === "mykad") {
              onClickMyKad();
            } else if (pendingAction === "singpass") {
              void handleTriggerSingpass();
            } else if (pendingAction === "esign") {
              setSignaturePadModal(true);
            }

            setPendingAction(null);
          }}
        />
      )}
    </Spin>
  );
};

export default PledgeConsentApproval;
